import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeSlotCard } from "@/components/TimeSlotCard";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertCircle, Clock, Info, Check, UtensilsCrossed, Loader2, Baby } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { supabase } from "@/integrations/supabase/client";
import { findOptimalTable, canPersonCountBeServed } from "@/lib/tableAssignmentUtils";
import { calculateEffectiveCapacityFromTotal } from "@/lib/childrenCapacityUtils";
import { generateAvailableTimes, countReservationsByTime, countPeopleByTime, normalizeTimeFormat } from "@/lib/timeBookingUtils";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { formatCustomerName } from "@/lib/clientUtils";
import { findParentSlotId, getEffectiveDuration, isWeeklyClosedDate } from "@/lib/timeSlotUtils";
import { addDays, isAfter, isBefore, startOfDay, isToday, addMinutes } from "date-fns";
import { fr } from "date-fns/locale";
import confetti from "canvas-confetti";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useTimeSlotLimits, resolveEffectiveLimit } from "@/hooks/useTimeSlotLimits";
import { getLargeGroupDisplay, shouldShowLargeGroupMessage } from "@/lib/largeGroupMessageUtils";
import { useEstablishmentClosures } from "@/hooks/useEstablishmentClosures";
import { useTimeSlotClosures } from "@/hooks/useTimeSlotClosures";
import { useTimeSlotBlocksQuery } from "@/hooks/queries/useTimeSlotBlocksQuery";
import { filterBlockedTimes, durationForTime, slotLabelMinutes } from "../../supabase/functions/_shared/timeBlocks";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { logger } from "@/lib/logger";
import { useEstablishmentBySlug } from "@/contexts/EstablishmentContext";
import { CookieConsentCheckbox } from "@/components/CookieConsentCheckbox";
import { Checkbox } from "@/components/ui/checkbox";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import { ZonePreferenceSelector } from "@/components/ZonePreferenceSelector";
import { useBookableZones } from "@/hooks/useBookableZones";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { getSlotsForZonePreference } from "@/lib/zoneScheduleUtils";
import { useTerraceClosures } from "@/hooks/useTerraceClosures";
import type { TerracePreference } from "@/types/table";
import { useEstablishmentNotices } from "@/hooks/useEstablishmentNotices";
import { selectApplicableNotice } from "@/lib/establishmentNoticeUtils";
import { EstablishmentNoticeAlert } from "@/components/EstablishmentNoticeAlert";
import { useEstablishmentQuestions } from "@/hooks/useEstablishmentQuestions";
import { selectApplicableQuestion, normalizeAnswers } from "@/lib/establishmentQuestionUtils";
import { EstablishmentQuestionField } from "@/components/EstablishmentQuestionField";
import { resolveBookingTitle } from "@/lib/bookingTitleUtils";
import { diagnoseBookingPlacement, diagnosePublicSlots, getPublicSlotsForPlacement, normalizePublicBookingParty, resolvePublicTerracePreference } from "@/lib/publicAvailability";
import { PlacementSelectionSummary } from "@/components/PlacementSelectionSummary";
import { usePublicPlacementSelection, useSelectedPlacementGuard } from "@/hooks/usePublicPlacementSelection";

const BookEmbed = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const { establishment, loading: establishmentLoading, error: establishmentError } = useEstablishmentBySlug(slug);
  
  const { 
    tables,
    combinedTables, 
    reservations, 
    availabilities, 
    weekdayAvailabilities,
    bookablePeriods,
    timeSlots: supabaseTimeSlots,
    dateOverrides,
    loading: dataLoading,
    refetch
  } = useSupabaseData({ 
    slug: slug,
    establishmentId: establishment?.id 
  });
  
  const { settings: reservationSettings, loading: settingsLoading } = useReservationSettings({ establishmentId: establishment?.id });
  const { limits: timeSlotLimits } = useTimeSlotLimits({ establishmentId: establishment?.id });
  const { closures, isDateInClosure, loading: closuresLoading } = useEstablishmentClosures({ establishmentId: establishment?.id });
  const { isSlotClosedOnDate, getSlotClosureMessage, getCustomSlotClosureMessage, loading: slotClosuresLoading } = useTimeSlotClosures({ establishmentId: establishment?.id });
  const { data: timeBlocks = [] } = useTimeSlotBlocksQuery(establishment?.id);
  const { isTerraceClosedOnDate } = useTerraceClosures({ establishmentId: establishment?.id });
  const { notices: establishmentNotices } = useEstablishmentNotices({
    establishmentId: establishment?.id,
    usePublicClient: true,
  });
  const { questions: establishmentQuestions } = useEstablishmentQuestions({
    establishmentId: establishment?.id,
    usePublicClient: true,
  });
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [noticeAcknowledged, setNoticeAcknowledged] = useState(false);
  const [questionAnswers, setQuestionAnswers] = useState<string[]>([]);
  const [persons, setPersons] = useState<string>("");
  const [numberOfChildren, setNumberOfChildren] = useState<number>(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');
  const terraceChoiceMade = !reservationSettings?.terraceEnabled || terracePreference !== 'no_preference';
  const effectiveTerracePreference = resolvePublicTerracePreference(reservationSettings?.terraceEnabled, terracePreference);
  const [zonePreference, setZonePreference] = useState<string[]>([]);
  const { zones: bookableZones } = useBookableZones({
    establishmentId: establishment?.id,
    useAuthenticatedData: false,
  });
  const { zoneWindows } = useZoneTimeWindows({
    establishmentId: establishment?.id,
    useAuthenticatedData: false,
  });
  const activeZoneIds = useMemo(
    () => new Set(bookableZones.filter(z => z.isActive).map(z => z.id)),
    [bookableZones]
  );
  // Note optionnelle de l'établissement (permanente, datée ou hebdomadaire)
  const applicableNotice = useMemo(
    () => selectApplicableNotice(establishmentNotices, date),
    [establishmentNotices, date]
  );
  useEffect(() => {
    setNoticeAcknowledged(false);
  }, [applicableNotice?.id]);
  const noticeBlocksSubmit = !!applicableNotice?.requireAcknowledgement && !noticeAcknowledged;

  // Question optionnelle posée au client (même logique de période que les notes)
  const applicableQuestion = useMemo(
    () => selectApplicableQuestion(establishmentQuestions, date),
    [establishmentQuestions, date]
  );
  useEffect(() => {
    setQuestionAnswers([]);
  }, [applicableQuestion?.id]);
  const normalizedAnswers = applicableQuestion
    ? normalizeAnswers(applicableQuestion, questionAnswers)
    : [];
  const questionBlocksSubmit = !!applicableQuestion?.isRequired && normalizedAnswers.length === 0;
  const activeZones = bookableZones.filter(z => z.isActive);
  const zonesUiEnabled = (reservationSettings?.zonesEnabled ?? false) && activeZones.length > 0;

  // Créneaux effectifs : horaires propres à la salle si toutes les salles choisies en ont, sinon calcul historique
  const slotsFor = useCallback(
    (d: Date) => getSlotsForZonePreference(
      d,
      zonesUiEnabled ? zonePreference : [],
      supabaseTimeSlots,
      dateOverrides,
      zoneWindows,
      activeZoneIds,
    ),
    [zonesUiEnabled, zonePreference, supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds]
  );
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [showNotes, setShowNotes] = useState(false);
  const [reservationConfirmed, setReservationConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [confirmedReservationDetails, setConfirmedReservationDetails] = useState<{
    name: string;
    date: string;
    time: string;
    persons: string;
  } | null>(null);
  const { toast } = useToast();
  const clearSelectedSlot = useCallback(() => setSelectedSlot(""), []);
  const { changeTerracePreference, changeZonePreference } = usePublicPlacementSelection({
    setTerracePreference,
    setZonePreference,
    clearSelectedSlot,
  });
  
  const loading = establishmentLoading || dataLoading || closuresLoading || slotClosuresLoading;
  const establishmentName = establishment?.name || 'Restaurant';
  const bookingTitle = resolveBookingTitle(reservationSettings?.bookingTitle, establishment?.name);
  const hasCustomBookingTitle = Boolean(reservationSettings?.bookingTitle?.trim());

  // Check if a date is in closure period
  const isDateDisabledByClosure = useCallback((date: Date): { disabled: boolean; reason?: string; customerMessage?: string } => {
    const result = isDateInClosure(date);
    if (result.inClosure) {
      return { disabled: true, reason: result.reason, customerMessage: result.customerMessage };
    }
    if (isWeeklyClosedDate(date, supabaseTimeSlots, dateOverrides)) {
      return { disabled: true, customerMessage: "L'établissement est fermé à cette date" };
    }
    return { disabled: false };
  }, [isDateInClosure, supabaseTimeSlots, dateOverrides]);

  // Reset selections when date changes
  useEffect(() => {
    if (!reservationSettings?.childrenEnabled) {
      setPersons("");
      setNumberOfChildren(0);
    }
    setSelectedSlot("");
  }, [date]);

  // When childrenEnabled: reset date and slot when persons or children change
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reservationSettings intentionally excluded to avoid realtime update triggers
  useEffect(() => {
    if (reservationSettings?.childrenEnabled) {
      setDate(undefined);
      setSelectedSlot("");
    }
  }, [persons, numberOfChildren]);

  // Clamp numberOfChildren when persons decreases
  useEffect(() => {
    if (reservationSettings?.childrenEnabled && persons && numberOfChildren >= parseInt(persons)) {
      setNumberOfChildren(Math.max(0, parseInt(persons) - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons]);


  // Map time slots from database, filtering by day of week and slot closures
  const allTimeSlots = useMemo(() => {
    if (!date) return [];
    
    const dateStr = formatDateLocal(date);
    const slotsForDate = slotsFor(date);
    
    const hasOverrides = dateOverrides.some(o => o.date === formatDateLocal(date) && o.isActive);
    const filteredSlots = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    
    const labels = filteredSlots.map(ts => `${ts.start} - ${ts.end}`);
    // Heures fermées manuellement (mode créneaux : le créneau commençant à l'heure fermée)
    if (reservationSettings?.bookingMode === 'time') return labels;
    return labels.filter(l => filterBlockedTimes(dateStr, [l.split(' - ')[0]], timeBlocks, () => slotLabelMinutes(l)).length > 0);
  }, [timeBlocks, reservationSettings?.bookingMode, slotsFor, dateOverrides, date, isSlotClosedOnDate]);

  // Resolved TimeSlot[] objects for the current date (used by getEffectiveDuration)
  const resolvedSlotsForDate = useMemo(() => {
    if (!date) return [];
    return slotsFor(date);
  }, [date, slotsFor]);

  // True when every slot applicable to this date is closed via time_slot_closures
  const allSlotsClosedForDate = useMemo(() => {
    if (!date) return false;
    const dateStr = formatDateLocal(date);
    const slotsForDate = slotsFor(date);
    if (slotsForDate.length === 0) return false;
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    if (hasOverrides) return false;
    return slotsForDate.every(ts => ts.id && isSlotClosedOnDate(ts.id, dateStr));
  }, [date, slotsFor, dateOverrides, isSlotClosedOnDate]);

  // Optional customer_message from at least one closed slot (no default fallback)
  const customSlotClosureMessage = useMemo(() => {
    if (!date || !allSlotsClosedForDate) return undefined;
    const dateStr = formatDateLocal(date);
    const slotsForDate = slotsFor(date);
    for (const ts of slotsForDate) {
      if (ts.id) {
        const msg = getCustomSlotClosureMessage(ts.id, dateStr);
        if (msg) return msg;
      }
    }
    return undefined;
  }, [date, allSlotsClosedForDate, slotsFor, getCustomSlotClosureMessage]);

  // Compute closure message when all slots are closed for the selected date
  const slotClosureMessage = useMemo(() => {
    if (!date) return undefined;
    const dateStr = formatDateLocal(date);
    const slotsForDate = slotsFor(date);
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const hasOpenSlot = hasOverrides
      ? slotsForDate.length > 0
      : slotsForDate.some(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    if (hasOpenSlot) return undefined;
    for (const ts of supabaseTimeSlots) {
      if (ts.id) {
        const msg = getSlotClosureMessage(ts.id, dateStr);
        if (msg) return msg;
      }
    }
    return undefined;
  }, [date, slotsFor, supabaseTimeSlots, dateOverrides, isSlotClosedOnDate, getSlotClosureMessage]);

  // Generate available booking times for TIME mode
  const availableBookingTimes = useMemo(() => {
    if (reservationSettings?.bookingMode !== 'time' || !date) return [];
    
    const dateStr = formatDateLocal(date);
    const slotsForDate = slotsFor(date);
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const filteredSlots = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });

    const slotRanges = filteredSlots.map(ts => ({
      start: ts.start,
      end: ts.end,
      durationOverrideMinutes: ts.durationOverrideMinutes,
    }));

    return filterBlockedTimes(dateStr, generateAvailableTimes(
      slotRanges,
      reservationSettings.bookingIntervalMinutes as 15 | 30 | 60,
      reservationSettings.reservationDurationMinutes
    ), timeBlocks, (t) => durationForTime(t, slotRanges, reservationSettings.reservationDurationMinutes));
  }, [date, reservationSettings, slotsFor, isSlotClosedOnDate, dateOverrides, timeBlocks]);

  // Shared SLOT diagnosis: same assignment engine as final confirmation.
  const timeSlotAvailability = useMemo(() => {
    if (!date) return [];
    const total = Number.parseInt(persons, 10);
    const party = normalizePublicBookingParty(Number.isFinite(total) ? total : 0, numberOfChildren, hasStroller, reservationSettings?.childrenEnabled ?? false, reservationSettings?.strollerEnabled ?? false);
    const bookingMode = reservationSettings?.bookingMode ?? 'slot';
    const reservationDuration = reservationSettings?.reservationDurationMinutes ?? 120;
    return diagnosePublicSlots({
      slots: allTimeSlots, date, party, childrenCapacityMode: reservationSettings?.childrenCapacityMode ?? 'full',
      terracePreference: effectiveTerracePreference, zonePreference: zonesUiEnabled ? zonePreference : [],
      tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => bookingMode === 'time' ? { bookingMode: 'time', reservationDurationMinutes: getEffectiveDuration(slot, resolvedSlotsForDate, reservationDuration), serviceSlots: resolvedSlotsForDate } : undefined,
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr, findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings?.customAssignmentOrderEnabled ?? false,
    });
  }, [date, persons, numberOfChildren, hasStroller, reservationSettings, allTimeSlots, effectiveTerracePreference, zonesUiEnabled, zonePreference, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, isTerraceClosedOnDate, supabaseTimeSlots]);

  // Count reservations by time for limit checks
  const reservationCountsByTime = useMemo(() => {
    if (reservationSettings?.bookingMode !== 'time' || !date) return new Map<string, number>();
    return countReservationsByTime(
      reservations.map(r => ({ 
        date: r.date, 
        startTime: r.startTime, 
        status: r.status 
      })), 
      date
    );
  }, [reservations, date, reservationSettings?.bookingMode]);

  // Count people by time for limit checks
  const peopleCountsByTime = useMemo(() => {
    if (reservationSettings?.bookingMode !== 'time' || !date) return new Map<string, number>();
    return countPeopleByTime(
      reservations.map(r => ({ 
        date: r.date, 
        startTime: r.startTime, 
        status: r.status,
        numberOfPeople: r.numberOfPeople,
        numberOfChildren: r.numberOfChildren
      })), 
      date
    );
  }, [reservations, date, reservationSettings?.bookingMode]);

  // Shared TIME diagnosis: same assignment engine as SLOT and final confirmation.
  const timeAvailability = useMemo(() => {
    if (reservationSettings?.bookingMode !== 'time' || !date) return [];
    const total = Number.parseInt(persons, 10);
    const party = normalizePublicBookingParty(Number.isFinite(total) ? total : 0, numberOfChildren, hasStroller, reservationSettings.childrenEnabled, reservationSettings.strollerEnabled);
    const timesWithinLimit = availableBookingTimes.filter(time => {
      const normalizedTime = normalizeTimeFormat(time);
      const effectiveLimit = resolveEffectiveLimit(date.getDay(), findParentSlotId(time, supabaseTimeSlots), timeSlotLimits, reservationSettings, normalizedTime);
      if (!effectiveLimit) return true;
      if (effectiveLimit.type === 'reservations') return (reservationCountsByTime.get(normalizedTime) || 0) < effectiveLimit.value;
      return party.total === 0 || (peopleCountsByTime.get(normalizedTime) || 0) + party.total <= effectiveLimit.value;
    });
    const now = new Date();
    const minTime = addMinutes(now, reservationSettings.minBookingMinutes);
    const cutoff = reservationSettings.sameDayCutoffTime;
    const cutoffReached = isToday(date) && !!cutoff && (() => { const [hours, minutes] = cutoff.substring(0, 5).split(':').map(Number); const value = new Date(); value.setHours(hours, minutes, 0, 0); return now >= value; })();
    return diagnosePublicSlots({
      slots: timesWithinLimit, date, party, childrenCapacityMode: reservationSettings.childrenCapacityMode, terracePreference: effectiveTerracePreference, zonePreference: zonesUiEnabled ? zonePreference : [],
      tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => ({ bookingMode: 'time', reservationDurationMinutes: getEffectiveDuration(slot, resolvedSlotsForDate, reservationSettings.reservationDurationMinutes), serviceSlots: resolvedSlotsForDate }),
      isHoursUnavailable: slot => cutoffReached || (isToday(date) && reservationSettings.minBookingMinutes > 0 && (() => { const [hours, minutes] = slot.split(':').map(Number); const value = new Date(); value.setHours(hours, minutes, 0, 0); return value < minTime; })()),
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr, findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings.customAssignmentOrderEnabled,
    });
  }, [date, persons, numberOfChildren, hasStroller, reservationSettings, availableBookingTimes, reservationCountsByTime, peopleCountsByTime, supabaseTimeSlots, timeSlotLimits, effectiveTerracePreference, zonesUiEnabled, zonePreference, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, isTerraceClosedOnDate]);

  // Add the same-day timing policy without recalculating table capacity.
  const filteredTimeSlots = useMemo(() => {
    if (!date) return timeSlotAvailability;
    const now = new Date();
    const minMinutes = reservationSettings?.minBookingMinutes ?? 0;
    const minTime = addMinutes(now, minMinutes);
    const cutoff = reservationSettings?.sameDayCutoffTime;
    const cutoffReached = isToday(date) && !!cutoff && (() => {
      const [hours, minutes] = cutoff.substring(0, 5).split(':').map(Number);
      const value = new Date(); value.setHours(hours, minutes, 0, 0); return now >= value;
    })();
    return timeSlotAvailability.map(slot => {
      const start = slot.time.split(' - ')[0];
      const [hours, minutes] = start.split(':').map(Number);
      const value = new Date(); value.setHours(hours, minutes, 0, 0);
      const disabledByHours = cutoffReached || (isToday(date) && minMinutes > 0 && value < minTime);
      return disabledByHours
        ? { ...slot, isAvailable: false, hasAvailability: false, unavailableReason: 'hours' as const, disabledByHours: true }
        : { ...slot, disabledByHours: false };
    });
  }, [timeSlotAvailability, date, reservationSettings?.minBookingMinutes, reservationSettings?.sameDayCutoffTime]);

  const placementAvailability = useMemo(() => {
    if (!date || !persons) return {};
    const total = Number.parseInt(persons, 10);
    if (!Number.isFinite(total) || total < 1) return {};
    const party = normalizePublicBookingParty(
      total, numberOfChildren, hasStroller,
      reservationSettings?.childrenEnabled ?? false,
      reservationSettings?.strollerEnabled ?? false,
    );
    const options = [
      ...(reservationSettings?.terraceEnabled ? [
        { key: 'indoor', terracePreference: 'indoor' as const, zonePreference: [] },
        { key: 'terrace', terracePreference: 'terrace' as const, zonePreference: [] },
      ] : []),
      ...(zonesUiEnabled ? activeZones.map(zone => ({ key: zone.id, terracePreference: 'no_preference' as const, zonePreference: [zone.id] })) : []),
    ];
    return diagnoseBookingPlacement({
      date, options,
      getSlots: option => getPublicSlotsForPlacement({
        date, zonePreference: option.zonePreference,
        bookingMode: reservationSettings?.bookingMode ?? 'slot',
        regularSlots: supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds,
        isSlotClosedOnDate, timeBlocks,
        bookingIntervalMinutes: reservationSettings?.bookingIntervalMinutes ?? 30,
        reservationDurationMinutes: reservationSettings?.reservationDurationMinutes ?? 120,
      }),
      adults: party.adults, numberOfChildren: party.children,
      childrenCapacityMode: (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none',
      hasStroller: party.hasStroller, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => reservationSettings?.bookingMode === 'time' ? { bookingMode: 'time', reservationDurationMinutes: getEffectiveDuration(slot, resolvedSlotsForDate, reservationSettings?.reservationDurationMinutes ?? 120), serviceSlots: resolvedSlotsForDate } : undefined,
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr || formatDateLocal(date), findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings?.customAssignmentOrderEnabled ?? false,
    });
  }, [date, persons, numberOfChildren, hasStroller, reservationSettings, zonesUiEnabled, activeZones, supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, isTerraceClosedOnDate, isSlotClosedOnDate, timeBlocks]);

  // Never rewrite the customer's placement: keep it, clear the time, hide the grid.
  const { selectedPlacementUnavailable } = useSelectedPlacementGuard({
    terracePreference, zonePreference, placementAvailability, clearTimeSelection: clearSelectedSlot,
  });
  const canShowTimes = terraceChoiceMade && !selectedPlacementUnavailable;

  // Calculate available person counts based on selected time slot
  const availablePersonCounts = useMemo(() => {
    if (!date) return [];
    const childrenCapacityMode = (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none';

    // Choisir la source de données selon le mode de réservation
    const isTimeMode = reservationSettings?.bookingMode === 'time';
    let maxCapacity = 0;

    if (isTimeMode) {
      // En mode TIME, utiliser timeAvailability qui contient les heures individuelles avec maxCapacity
      let relevantTimes = timeAvailability;
      if (selectedSlot) {
        relevantTimes = timeAvailability.filter(t => t.time === selectedSlot);
      }
      maxCapacity = Math.max(...relevantTimes.map(t => t.maxCapacity), 0);
    } else {
      // En mode SLOT, utiliser timeSlotAvailability
      let relevantSlots = timeSlotAvailability;
      if (selectedSlot) {
        relevantSlots = timeSlotAvailability.filter(s => s.time === selectedSlot);
      }
      maxCapacity = Math.max(...relevantSlots.map(s => s.maxCapacity), 0);
    }

    if (maxCapacity === 0) return [];

    // Appliquer les limites min/max de reservation_settings (alignement avec Book.tsx)
    const minPeople = reservationSettings?.minPeople ?? 1;
    const maxPeople = reservationSettings?.maxPeople ?? 20;
    const effectiveMax = Math.min(maxCapacity, maxPeople);

    if (minPeople > effectiveMax) return [];

    return Array.from({ length: effectiveMax - minPeople + 1 }, (_, i) => {
      const count = minPeople + i;
      let isAvailable = false;
      
      if (isTimeMode) {
        const relevantTimes = selectedSlot 
          ? timeAvailability.filter(t => t.time === selectedSlot)
          : timeAvailability;
        const effectiveOccupancy = calculateEffectiveCapacityFromTotal(count, numberOfChildren, childrenCapacityMode);
        isAvailable = relevantTimes.some(t => t.isAvailable && canPersonCountBeServed(count, t.singleCapacities, t.combinedRawCapacities, t.combinedEffectiveCapacities, t.singleMinCapacities, t.combinedMinCapacities, effectiveOccupancy));
      } else {
        const relevantSlots = selectedSlot 
          ? timeSlotAvailability.filter(s => s.time === selectedSlot)
          : timeSlotAvailability;
        const effectiveOccupancy = calculateEffectiveCapacityFromTotal(count, numberOfChildren, childrenCapacityMode);
        isAvailable = relevantSlots.some(s => s.hasAvailability && canPersonCountBeServed(count, s.singleCapacities, s.combinedRawCapacities, s.combinedEffectiveCapacities, s.singleMinCapacities, s.combinedMinCapacities, effectiveOccupancy));
      }
      
      return {
        count,
        isAvailable
      };
    });
  }, [date, selectedSlot, timeSlotAvailability, timeAvailability, reservationSettings?.minPeople, reservationSettings?.maxPeople, reservationSettings?.bookingMode, reservationSettings?.childrenCapacityMode, numberOfChildren]);

  // Static max capacity (no date filter) for children ON person selector capping
  const staticMaxCapacity = useMemo(() => {
    const bookableTables = tables.filter(t => t.isActive && t.isBookable !== false);
    const bookableCombined = combinedTables.filter(ct => ct.isActive && ct.isBookable !== false);

    if (effectiveTerracePreference === 'no_preference') {
      const maxSingle = bookableTables.length > 0 ? Math.max(...bookableTables.map(t => t.capacity)) : 0;
      const maxCombined = bookableCombined.length > 0 ? Math.max(...bookableCombined.map(ct => ct.totalCapacity)) : 0;
      return Math.max(maxSingle, maxCombined);
    }

    const terraceFilter = (t: { isTerrace?: boolean }) =>
      effectiveTerracePreference === 'terrace' ? t.isTerrace === true : t.isTerrace !== true;

    const preferredSingle = bookableTables.filter(terraceFilter);
    const preferredCombined = bookableCombined.filter(terraceFilter);

    // Fallback global : seulement si AUCUNE table (simple ou combinée) ne correspond
    const hasAnyPreferred = preferredSingle.length > 0 || preferredCombined.length > 0;
    const finalSingle = hasAnyPreferred ? preferredSingle : bookableTables;
    const finalCombined = hasAnyPreferred ? preferredCombined : bookableCombined;

    const maxSingle = finalSingle.length > 0 ? Math.max(...finalSingle.map(t => t.capacity)) : 0;
    const maxCombined = finalCombined.length > 0 ? Math.max(...finalCombined.map(ct => ct.totalCapacity)) : 0;
    return Math.max(maxSingle, maxCombined);
  }, [tables, combinedTables, effectiveTerracePreference]);


  useEffect(() => {
    if (reservationConfirmed) return;
    if (persons && availablePersonCounts.length > 0) {
      const currentPersons = parseInt(persons);
      const isStillAvailable = availablePersonCounts.some(
        p => p.count === currentPersons && p.isAvailable
      );
      if (!isStillAvailable && !reservationSettings?.childrenEnabled) {
        setPersons("");
      }
    }
  }, [availablePersonCounts, persons, reservationConfirmed]);

  // Re-validate selected slot when persons changes
  useEffect(() => {
    if (!selectedSlot || !persons || reservationConfirmed) return;
    const isTimeMode = reservationSettings?.bookingMode === 'time';
    if (isTimeMode) {
      const isStillAvailable = timeAvailability.some(
        t => t.time === selectedSlot && t.isAvailable
      );
      if (!isStillAvailable) {
        setSelectedSlot("");
        toast({
          description: "L'horaire sélectionné n'est plus disponible pour ce nombre de personnes.",
          duration: 4000,
        });
      }
    } else {
      const isStillAvailable = timeSlotAvailability.some(
        s => s.time === selectedSlot && s.hasAvailability
      );
      if (!isStillAvailable) {
        setSelectedSlot("");
        toast({
          description: "Le créneau sélectionné n'est plus disponible pour ce nombre de personnes.",
          duration: 4000,
        });
      }
    }
  }, [persons, timeAvailability, timeSlotAvailability]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confirmation de lecture de la note obligatoire (bloque uniquement le formulaire)
    if (noticeBlocksSubmit) {
      toast({
        title: "Confirmation requise",
        description: "Merci de confirmer que vous avez lu la note de l'établissement.",
        variant: "destructive",
      });
      return;
    }

    // Question obligatoire de l'établissement
    if (questionBlocksSubmit) {
      toast({
        title: "Réponse requise",
        description: "Merci de répondre à la question de l'établissement.",
        variant: "destructive",
      });
      return;
    }

    // Placement explicite obligatoire : jamais de repli silencieux vers « sans préférence »
    if (!canShowTimes || !selectedSlot) {
      toast({
        title: "Placement requis",
        description: "Merci de choisir un placement disponible, puis un horaire.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    const formData = new FormData(e.target as HTMLFormElement);
    const firstName = formData.get("firstName") as string;
    const lastName = formData.get("lastName") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const notes = formData.get("notes") as string || undefined;

    if (reservationSettings?.requirePhone && !phone?.trim()) {
      setIsSubmitting(false);
      toast({
        title: "Téléphone requis",
        description: "Merci d'indiquer votre numéro de téléphone.",
        variant: "destructive",
      });
      return;
    }
    
    // Prepare conflict options for TIME mode
    const bookingMode = reservationSettings?.bookingMode ?? 'slot';
    const reservationDuration = reservationSettings?.reservationDurationMinutes ?? 120;
    const conflictOptions = bookingMode === 'time' 
      ? { bookingMode: 'time' as const, reservationDurationMinutes: reservationDuration, serviceSlots: resolvedSlotsForDate }
      : undefined;

    // Determine the start time to use (TIME mode uses direct time, SLOT mode extracts from slot)
    const startTime = bookingMode === 'time' ? selectedSlot : selectedSlot.split(' - ')[0];

    const childrenCapacityMode = (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none';
    const adultCount = reservationSettings?.childrenEnabled
      ? parseInt(persons) - numberOfChildren
      : parseInt(persons);
    // Utiliser les données en mémoire (le serveur valide tout côté Edge Function)
    const optimalTable = findOptimalTable(
      adultCount,
      date!,
      selectedSlot,
      tables,
      combinedTables,
      reservations,
      availabilities,
      weekdayAvailabilities,
      conflictOptions,
      bookablePeriods,
      numberOfChildren,
      childrenCapacityMode,
      hasStroller,
      effectiveTerracePreference,
      (dateStr) => isTerraceClosedOnDate(dateStr, findParentSlotId(selectedSlot, supabaseTimeSlots)),
      reservationSettings?.customAssignmentOrderEnabled ?? false,
      zonesUiEnabled ? zonePreference : []
    );

    if (!optimalTable) {
      setIsSubmitting(false);
      toast({
        title: "Aucune table disponible",
        description: "Désolé, aucune table n'est disponible pour ce créneau. Essayez un autre horaire.",
        variant: "destructive",
        duration: 6000,
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-reservation', {
        body: {
          slug: slug,
          establishmentId: establishment?.id,
          customer: {
            email: email || null,
            firstName,
             lastName,
             phone: phone?.trim() || null,
             marketingConsent,
             smsConsent,
           },
          reservation: {
            date: formatDateLocal(date!),
            startTime: startTime,
            tableId: optimalTable.type === 'single' ? optimalTable.id : null,
            combinedTableId: optimalTable.type === 'combined' ? optimalTable.id : null,
            numberOfPeople: adultCount,
            numberOfChildren: numberOfChildren,
            hasStroller: hasStroller,
            terracePreference: terracePreference,
            zonePreference: zonesUiEnabled && zonePreference.length > 0 ? zonePreference : undefined,
            notes: notes && notes.trim() !== '' ? notes.trim() : undefined,
            customAnswers:
              applicableQuestion && normalizedAnswers.length > 0
                ? { question: applicableQuestion.question, answers: normalizedAnswers }
                : undefined,
          }
        }
      });

      if (error) {
        const serverMessage = data?.error;
        throw new Error(serverMessage || "Impossible de créer la réservation. Veuillez réessayer.");
      }

      const formattedDate = date?.toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      });
      
      setConfirmedReservationDetails({
        name: formatCustomerName(firstName, lastName),
        date: formattedDate || "",
        time: selectedSlot,
        persons,
      });
      
      setIsSubmitting(false);
      setReservationConfirmed(true);
      
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      toast({
        title: "🎉 Merci pour votre réservation !",
        description: `Votre table pour ${persons} personne${persons !== "1" ? "s" : ""} est confirmée.`,
        duration: 6000,
      });
      
      refetch();
    } catch (error: any) {
      logger.error('Erreur création réservation:', error);
      setIsSubmitting(false);
      await refetch();
      setSelectedSlot("");
      toast({
        title: "Horaire indisponible",
        description: "Cet horaire vient peut-être d’être réservé. Les disponibilités ont été actualisées, veuillez choisir un autre horaire.",
        variant: "destructive",
        duration: 6000,
      });
    }
  };

  const handleNewReservation = () => {
    setReservationConfirmed(false);
    setConfirmedReservationDetails(null);
    setDate(undefined);
    setPersons("");
    setNumberOfChildren(0);
    setHasStroller(false);
    setTerracePreference('no_preference');
    setZonePreference([]);
    setSelectedSlot("");
    setShowNotes(false);
    setMarketingConsent(false);
    setSmsConsent(false);
  };

  // Afficher erreur si établissement non trouvé
  if (slug && establishmentError) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[300px]">
        <Card className="p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-2">Réservations indisponibles</h2>
          <p className="text-muted-foreground text-sm">
            Cet établissement ne prend pas de réservations en ligne pour le moment.
          </p>
        </Card>
      </div>
    );
  }

  if (loading || settingsLoading || !reservationSettings) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="animate-pulse">
              <UtensilsCrossed className="w-10 h-10 text-primary" />
            </div>
          </div>
          <p className="mt-4 text-muted-foreground text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {reservationSettings?.isPaused && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="font-semibold">
            Réservations indisponibles
          </AlertTitle>
          <AlertDescription className="text-sm mt-1">
            {reservationSettings.pauseMessage || 
              "Nous ne prenons pas de réservations en ce moment. Veuillez nous contacter directement par téléphone."}
          </AlertDescription>
        </Alert>
      )}

      {reservationConfirmed && confirmedReservationDetails ? (
        <Card className="p-6 text-center space-y-4">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-serif font-bold mb-2">Réservation confirmée !</h2>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-left max-w-sm mx-auto">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground text-sm">Date :</span>
                <span className="font-medium text-sm text-right">{confirmedReservationDetails.date}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground text-sm">Heure :</span>
                <span className="font-medium text-sm">{confirmedReservationDetails.time}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground text-sm">Personnes :</span>
                <span className="font-medium text-sm">
                  {numberOfChildren > 0
                    ? `${confirmedReservationDetails.persons} personne${confirmedReservationDetails.persons !== "1" ? "s" : ""} dont ${numberOfChildren} enfant${numberOfChildren > 1 ? "s" : ""}`
                    : `${confirmedReservationDetails.persons} personne${confirmedReservationDetails.persons !== "1" ? "s" : ""}`}
                </span>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Un email de confirmation vous a été envoyé.
          </p>
          <Button onClick={handleNewReservation} variant="outline" size="sm">
            Nouvelle réservation
          </Button>
        </Card>
      ) : (
        <div className={reservationSettings?.isPaused ? "pointer-events-none opacity-50" : ""}>
          <form onSubmit={handleSubmit}>
            <Card className="p-4 space-y-4">
              <div className="text-center pb-2 border-b">
                <h2 className="font-serif font-bold text-lg">
                  {hasCustomBookingTitle ? bookingTitle : establishmentName}
                </h2>
                {!hasCustomBookingTitle && (
                  <p className="text-muted-foreground text-sm">Réservez votre table</p>
                )}
              </div>

              {/* When childrenEnabled: Persons + Children FIRST */}
              {reservationSettings?.childrenEnabled && (
                <div className="space-y-2">
                  {/* Terrace preference FIRST (children ON flow) */}
                  {reservationSettings?.terraceEnabled && (
                    <div className="mb-3 pb-3 border-b">
                      <TerracePreferenceSelector
                        value={terracePreference}
                       onChange={changeTerracePreference}
                        helpText={reservationSettings?.terraceHelpText}
                        compact
                        hideNoPreference
                        availability={placementAvailability}
                      />
                    </div>
                  )}

                  {zonesUiEnabled && (
                    <div className="mb-3 pb-3 border-b">
                      <ZonePreferenceSelector
                        zones={activeZones}
                        value={zonePreference}
                       onChange={changeZonePreference}
                        mode={reservationSettings?.zonesSelectionMode ?? 'single'}
                        helpText={reservationSettings?.zonesHelpText}
                        compact
                        allowNoPreference={!(reservationSettings?.zonesStrict ?? true) || (reservationSettings?.zonesSelectionMode ?? 'single') === 'multi'}
                        availability={placementAvailability}
                      />
                    </div>
                  )}

                  {terraceChoiceMade && (
                  <>
                  <Label className="text-sm font-semibold">Nombre de personnes (enfants inclus)</Label>
                  <p className="text-xs text-muted-foreground">
                    Sélectionnez le nombre total de personnes
                  </p>
                  {(() => {
                    const minPeople = reservationSettings?.minPeople ?? 1;
                    const maxPeople = reservationSettings?.maxPeople ?? 20;
                    const effectiveMaxPeople = staticMaxCapacity > 0
                      ? Math.min(maxPeople, staticMaxCapacity)
                      : maxPeople;
                    const allCounts = Array.from({ length: effectiveMaxPeople - minPeople + 1 }, (_, i) => minPeople + i);
                    return allCounts.length <= 10 ? (
                      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                        {allCounts.map((count) => (
                          <button
                            key={count}
                            type="button"
                            onClick={() => setPersons(count.toString())}
                            className={`p-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${
                              persons === count.toString()
                                ? "border-primary bg-primary text-primary-foreground shadow-md"
                                : "border-border hover:border-primary/50 hover:bg-accent"
                            }`}
                          >
                            {count}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <Select value={persons} onValueChange={setPersons}>
                        <SelectTrigger className="w-full text-sm">
                          <SelectValue placeholder="Nombre de personnes" />
                        </SelectTrigger>
                        <SelectContent>
                          {allCounts.map((count) => (
                            <SelectItem key={count} value={count.toString()}>
                              {count} pers.
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}

                  {/* Large group message - right after people selector */}
                  {shouldShowLargeGroupMessage(reservationSettings) && reservationSettings && (() => {
                    const display = getLargeGroupDisplay(reservationSettings);
                    if (!display) return null;
                    return (
                      <p className="text-xs text-muted-foreground mt-3">
                        {display.message}{' '}
                        <a 
                          href={display.href}
                          className="text-primary underline"
                        >
                          {display.contact}
                        </a>
                      </p>
                    );
                  })()}

                  {/* Children selector inline */}
                  {persons && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Baby className="w-4 h-4 text-primary" />
                         <Label className="text-sm font-semibold">Dont combien d'enfants ?</Label>
                       </div>
                        <p className="text-xs text-muted-foreground">
                          {reservationSettings?.childrenHelpText || 'Indiquez combien de personnes dans votre groupe sont des enfants.'}
                        </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setNumberOfChildren(Math.max(0, numberOfChildren - 1))}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-border hover:border-primary/50 font-semibold text-sm transition-all"
                          disabled={numberOfChildren === 0}
                        >
                          -
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{numberOfChildren}</span>
                         <button
                           type="button"
                           onClick={() => setNumberOfChildren(Math.min(parseInt(persons) - 1, numberOfChildren + 1))}
                           className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-border hover:border-primary/50 font-semibold text-sm transition-all"
                           disabled={numberOfChildren >= parseInt(persons) - 1}
                        >
                          +
                        </button>
                      </div>

                      {/* Stroller checkbox */}
                      {reservationSettings?.strollerEnabled && (
                        <div className="mt-3 pt-2 border-t">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="stroller-embed"
                              checked={hasStroller}
                              onCheckedChange={(checked) => setHasStroller(checked === true)}
                            />
                            <div>
                              <Label htmlFor="stroller-embed" className="text-xs font-medium cursor-pointer">
                                Je viens avec une poussette
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                {reservationSettings?.strollerHelpText || 'Cochez si vous venez avec une poussette.'}
                              </p>
                            </div>
                          </div>
                    </div>
                  )}

                  </div>
                  )}
                  </>
                  )}
                </div>
              )}

              {/* Calendar - always visible when NOT childrenEnabled, or when persons selected when childrenEnabled */}
              {(!reservationSettings?.childrenEnabled || persons) && (
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(selectedDate) => {
                      if (selectedDate) {
                        const closureCheck = isDateDisabledByClosure(selectedDate);
                        if (closureCheck.disabled) {
                          toast({
                            title: "Date indisponible",
                            description: <span className="whitespace-pre-line">{closureCheck.customerMessage || "L'établissement est fermé à cette date"}</span>,
                            variant: "destructive",
                          });
                          return;
                        }
                      }
                      setDate(selectedDate);
                    }}
                    locale={fr}
                    weekStartsOn={1}
                    disabled={(d) => {
                      const today = startOfDay(new Date());
                      const minDate = addDays(today, reservationSettings?.minBookingDays || 0);
                      const maxDate = addDays(today, reservationSettings?.maxBookingDays || 90);
                      
                      if (isBefore(d, minDate)) return true;
                      if (isAfter(d, maxDate)) return true;

                      // Same-day cutoff: if cutoff time is set and we're past it, disable today
                      const cutoffTime = reservationSettings?.sameDayCutoffTime;
                      if (cutoffTime && isToday(d)) {
                        const now = new Date();
                        const [ch, cm] = cutoffTime.substring(0, 5).split(':').map(Number);
                        const cutoffDate = new Date();
                        cutoffDate.setHours(ch, cm, 0, 0);
                        if (now >= cutoffDate) return true;
                      }
                      
                      const closureCheck = isDateDisabledByClosure(d);
                      return closureCheck.disabled;
                    }}
                    modifiers={{
                      closure: (d) => isDateDisabledByClosure(d).disabled,
                    }}
                    modifiersStyles={{
                      closure: {
                        textDecoration: 'line-through',
                        opacity: 0.5,
                      }
                    }}
                    className="rounded-md border pointer-events-auto"
                  />
                </div>
              )}

              {/* Note optionnelle de l'établissement (après la date, avant les disponibilités) */}
              {date && applicableNotice && (
                <EstablishmentNoticeAlert
                  notice={applicableNotice}
                  showAcknowledgement
                  acknowledged={noticeAcknowledged}
                  onAcknowledgedChange={setNoticeAcknowledged}
                  compact
                  idPrefix="embed"
                />
              )}

              {/* Terrace preference (children OFF flow) - shown after date, before persons */}
              {date && !reservationSettings?.childrenEnabled && reservationSettings?.terraceEnabled && (
                <div className="space-y-2">
                  <TerracePreferenceSelector
                    value={terracePreference}
                    onChange={changeTerracePreference}
                    helpText={reservationSettings?.terraceHelpText}
                    compact
                    hideNoPreference
                    availability={placementAvailability}
                  />
                </div>
              )}

              {/* Zone preference (children OFF flow) */}
              {date && !reservationSettings?.childrenEnabled && zonesUiEnabled && (
                <div className="space-y-2">
                  <ZonePreferenceSelector
                    zones={activeZones}
                    value={zonePreference}
                    onChange={changeZonePreference}
                    mode={reservationSettings?.zonesSelectionMode ?? 'single'}
                    helpText={reservationSettings?.zonesHelpText}
                    compact
                    allowNoPreference={!(reservationSettings?.zonesStrict ?? true) || (reservationSettings?.zonesSelectionMode ?? 'single') === 'multi'}
                    availability={placementAvailability}
                  />
                </div>
              )}

              {/* Stroller checkbox (children OFF flow) */}
              {date && !reservationSettings?.childrenEnabled && reservationSettings?.strollerEnabled && (
                <div className="flex items-center space-x-2 py-2">
                  <Checkbox
                    id="stroller-no-children-embed"
                    checked={hasStroller}
                    onCheckedChange={(checked) => setHasStroller(checked === true)}
                  />
                  <div>
                    <Label htmlFor="stroller-no-children-embed" className="text-xs font-medium cursor-pointer">
                      Je viens avec une poussette
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {reservationSettings?.strollerHelpText || 'Cochez si vous venez avec une poussette.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Standard flow (no children): date -> persons -> children -> slots */}
              {date && !reservationSettings?.childrenEnabled && terraceChoiceMade && (
                <>
                  {/* Number of people */}
                  <div className="space-y-2">
                    {!allSlotsClosedForDate && (
                      <>
                        <Label className="text-sm font-semibold">Nombre de personnes</Label>
                        <p className="text-xs text-muted-foreground">
                          {selectedSlot 
                            ? `Places disponibles pour ${selectedSlot}` 
                            : "Sélectionnez pour voir les créneaux"}
                        </p>
                      </>
                    )}
                    
                    {allSlotsClosedForDate ? (
                      <div className="space-y-2">
                        {customSlotClosureMessage && (
                          <Label className="text-sm font-semibold whitespace-pre-line">
                            {customSlotClosureMessage}
                          </Label>
                        )}
                        <div className="text-xs bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 p-3 rounded-lg">
                          😔 Aucune table disponible pour cette date.
                        </div>
                      </div>
                    ) : availablePersonCounts.filter(p => p.isAvailable).length > 0 ? (
                      <div className="space-y-2">
                        {availablePersonCounts.filter(p => p.isAvailable).length <= 10 ? (
                          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                            {availablePersonCounts
                              .filter(({ isAvailable }) => isAvailable)
                              .map(({ count }) => (
                              <button
                                key={count}
                                type="button"
                                onClick={() => setPersons(count.toString())}
                                className={`p-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${
                                  persons === count.toString()
                                    ? "border-primary bg-primary text-primary-foreground shadow-md"
                                    : "border-border hover:border-primary/50 hover:bg-accent"
                                }`}
                              >
                                {count}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <Select value={persons} onValueChange={setPersons}>
                            <SelectTrigger className="w-full text-sm">
                              <SelectValue placeholder="Nombre de personnes" />
                            </SelectTrigger>
                            <SelectContent>
                              {availablePersonCounts
                                .filter(({ isAvailable }) => isAvailable)
                                .map(({ count }) => (
                                <SelectItem 
                                  key={count} 
                                  value={count.toString()}
                                >
                                  {count} pers.
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        {/* Large group message */}
                        {shouldShowLargeGroupMessage(reservationSettings) && reservationSettings && (() => {
                          const display = getLargeGroupDisplay(reservationSettings);
                          if (!display) return null;
                          return (
                            <p className="text-xs text-muted-foreground mt-3">
                              {display.message}{' '}
                              <a 
                                href={display.href}
                                className="text-primary underline"
                              >
                                {display.contact}
                              </a>
                            </p>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="text-xs bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 p-3 rounded-lg">
                        😔 Aucune table disponible pour cette date.
                      </div>
                    )}
                  </div>




                  {/* Time Slots — only when at least one service is open and persons selected */}
                  {!allSlotsClosedForDate && persons && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-primary" />
                        <Label className="text-sm font-semibold">
                          {reservationSettings?.bookingMode === 'time' ? 'Heure' : 'Créneau horaire'}
                        </Label>
                      </div>
                   <PlacementSelectionSummary
                     terracePreference={terracePreference}
                     terraceEnabled={reservationSettings?.terraceEnabled ?? false}
                     compact
                     unavailable={selectedPlacementUnavailable}
                   />
{!selectedPlacementUnavailable && (
                      <p className="text-xs text-muted-foreground">
                        {reservationSettings?.bookingMode === 'time'
                          ? `Heures pour ${persons} pers.`
                          : `Créneaux pour ${persons} personne${persons !== "1" ? "s" : ""}`}
                      </p>
)}
                      
                      {selectedPlacementUnavailable ? null : reservationSettings?.bookingMode === 'time' ? (
                        <TimeBookingGrid
                          availableTimes={timeAvailability}
                          selectedTime={selectedSlot}
                          onTimeSelect={setSelectedSlot}
                          disabled={!persons}
                          closureMessage={slotClosureMessage}
                        />
                      ) : filteredTimeSlots.length === 0 ? (
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{slotClosureMessage || 'Aucun créneau disponible'}</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {filteredTimeSlots.map((slot) => (
                            <TimeSlotCard
                              key={slot.time}
                              time={slot.time}
                              available={slot.available}
                              disabled={!slot.hasAvailability}
                              selected={selectedSlot === slot.time}
                              onClick={() => slot.hasAvailability && setSelectedSlot(slot.time)}
                              unavailableReason={slot.unavailableReason}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Children-enabled flow: time slots after date selection */}
              {date && reservationSettings?.childrenEnabled && terraceChoiceMade && (
                allSlotsClosedForDate ? (
                  <div className="space-y-2">
                    {customSlotClosureMessage && (
                      <Label className="text-sm font-semibold whitespace-pre-line">
                        {customSlotClosureMessage}
                      </Label>
                    )}
                    <div className="text-xs bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 p-3 rounded-lg">
                      😔 Aucune table disponible pour cette date.
                    </div>
                  </div>
                ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-primary" />
                    <Label className="text-sm font-semibold">
                      {reservationSettings?.bookingMode === 'time' ? 'Heure' : 'Créneau horaire'}
                    </Label>
                  </div>
                   <PlacementSelectionSummary
                     terracePreference={terracePreference}
                     terraceEnabled={reservationSettings?.terraceEnabled ?? false}
                     compact
                     unavailable={selectedPlacementUnavailable}
                   />
{!selectedPlacementUnavailable && (
                  <p className="text-xs text-muted-foreground">
                    {reservationSettings?.bookingMode === 'time'
                     ? `Heures pour ${persons} pers.${numberOfChildren > 0 ? ` (dont ${numberOfChildren} enfant${numberOfChildren > 1 ? "s" : ""})` : ""}`
                     : `Créneaux pour ${persons} personne${persons !== "1" ? "s" : ""}${numberOfChildren > 0 ? ` (dont ${numberOfChildren} enfant${numberOfChildren > 1 ? "s" : ""})` : ""}`}
                  </p>
)}
                  
                  {selectedPlacementUnavailable ? null : reservationSettings?.bookingMode === 'time' ? (
                    <TimeBookingGrid
                      availableTimes={timeAvailability}
                      selectedTime={selectedSlot}
                      onTimeSelect={setSelectedSlot}
                      disabled={false}
                      closureMessage={slotClosureMessage}
                    />
                  ) : filteredTimeSlots.length === 0 ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{slotClosureMessage || 'Aucun créneau disponible'}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {filteredTimeSlots.map((slot) => (
                        <TimeSlotCard
                          key={slot.time}
                          time={slot.time}
                          available={slot.available}
                          disabled={!slot.hasAvailability}
                          selected={selectedSlot === slot.time}
                          onClick={() => slot.hasAvailability && setSelectedSlot(slot.time)}
                          unavailableReason={slot.unavailableReason}
                        />
                      ))}
                    </div>
                  )}
                </div>
                )
              )}

              {/* Customer info - shown when all selections made */}
              {date && persons && selectedSlot && (
                    <div className="space-y-3 pt-2 border-t">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label htmlFor="firstName" className="text-xs">Prénom *</Label>
                          <Input id="firstName" name="firstName" required className="h-9" />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="lastName" className="text-xs">Nom *</Label>
                          <Input id="lastName" name="lastName" required className="h-9" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="email" className="text-xs">Email *</Label>
                        <Input id="email" name="email" type="email" required className="h-9" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="phone" className="text-xs">Téléphone{reservationSettings?.requirePhone ? ' *' : ''}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          maxLength={30}
                          required={reservationSettings?.requirePhone ?? false}
                          className="h-9"
                        />
                      </div>

                      <Accordion type="single" collapsible>
                        <AccordionItem value="notes" className="border-none">
                          <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                            Ajouter une remarque (optionnel)
                          </AccordionTrigger>
                          <AccordionContent>
                            <Textarea
                              name="notes"
                              placeholder="Allergies, occasion spéciale..."
                              className="min-h-[60px]"
                            />
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* RGPD notice */}
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        🔒 Vos informations seront transmises à{' '}
                        <strong>{establishment?.name}</strong> pour gérer votre réservation.
                      </p>

                      {/* Marketing consent checkboxes */}
                      {reservationSettings?.emailMarketingEnabled && (
                        <div className="flex items-start space-x-2 mt-2">
                          <Checkbox
                            id="marketing-consent"
                            checked={marketingConsent}
                            onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                            className="mt-0.5"
                          />
                          <Label 
                            htmlFor="marketing-consent" 
                            className="text-[11px] text-muted-foreground leading-tight cursor-pointer"
                          >
                            Envoyez-moi des offres et actualités par e-mail.{' '}
                            <span className="text-[10px]">Vous pourrez vous désinscrire à tout moment.</span>
                          </Label>
                        </div>
                      )}

                      {reservationSettings?.smsMarketingEnabled && (
                        <div className="flex items-start space-x-2 mt-2">
                          <Checkbox
                            id="sms-consent"
                            checked={smsConsent}
                            onCheckedChange={(checked) => setSmsConsent(checked === true)}
                            className="mt-0.5"
                          />
                          <Label 
                            htmlFor="sms-consent" 
                            className="text-[11px] text-muted-foreground leading-tight cursor-pointer"
                          >
                            Envoyez-moi des offres et actualités par SMS.{' '}
                            <span className="text-[10px]">Vous pourrez vous désinscrire à tout moment.</span>
                          </Label>
                        </div>
                      )}

                      {/* Cookie consent checkbox */}
                      <div className="mt-2">
                        <CookieConsentCheckbox />
                      </div>

                      {/* Question de l'établissement */}
                      {applicableQuestion && (
                        <EstablishmentQuestionField
                          question={applicableQuestion}
                          answers={questionAnswers}
                          onAnswersChange={setQuestionAnswers}
                          compact
                          idPrefix="embed"
                        />
                      )}

                      {/* Booking notice */}
                      {establishment?.bookingNotice && (
                        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
                          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <AlertDescription 
                            className="text-blue-800 dark:text-blue-300 text-xs whitespace-pre-wrap"
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {establishment.bookingNotice}
                          </AlertDescription>
                        </Alert>
                      )}

                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={isSubmitting || reservationSettings?.isPaused || noticeBlocksSubmit || questionBlocksSubmit}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Réservation...
                          </>
                        ) : (
                          "Confirmer la réservation"
                        )}
                      </Button>
                      <div className="text-center pt-3">
                        <a 
                          href="https://piktable.ch" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Réservations par Piktable 🇨🇭
                        </a>
                      </div>
                    </div>
                  )}
            </Card>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookEmbed;
